import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { Modal } from "../components/ui";
import { X, Search, Download, FileText, AlertTriangle, ChevronRight, FilePlus, ExternalLink } from "lucide-react";

const AUTHORITIES = [
  { key: "PROFEPA", label: "PROFEPA", desc: "Inspecciones, RACs, informes semestrales" },
  { key: "SEMARNAT", label: "SEMARNAT", desc: "MIA, ETJ, autorizaciones" },
  { key: "CONAGUA", label: "CONAGUA", desc: "Pozos, descargas, informes anuales" },
  { key: "ASEA", label: "ASEA", desc: "Almacenamiento diesel, trasiego" },
  { key: "AYUNTAMIENTO", label: "Ayuntamiento", desc: "EDA, PCO, Plan RSU" },
  { key: "CNSNS", label: "CNSNS", desc: "Material radiactivo" },
  { key: "ALL", label: "Todas", desc: "Vista consolidada" },
];

export default function AuditMode() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [authority, setAuthority] = useState("PROFEPA");
  const [search, setSearch] = useState("");
  const [searchHits, setSearchHits] = useState(null);
  const [summary, setSummary] = useState(null);
  const [permits, setPermits] = useState([]);
  const [recent, setRecent] = useState({});
  const [pdfPreview, setPdfPreview] = useState(null); // { url, name }
  const [tenant, setTenant] = useState(null);
  const [showActaForm, setShowActaForm] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 60);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      const [s, p, me] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/data/legal_permits"),
        api.get("/auth/me"),
      ]);
      setSummary(s.data);
      setPermits(p.data);
      setTenant(me.data?.tenant || {});
    })();
  }, []);

  // Load recent records per authority when authority changes
  useEffect(() => {
    if (authority === "ALL") return;
    (async () => {
      try {
        const [reports, racs, walks, wells, wellReads] = await Promise.all([
          api.get("/data/periodic_reports").catch(() => ({ data: [] })),
          api.get("/data/racs").catch(() => ({ data: [] })),
          api.get("/data/field_walks").catch(() => ({ data: [] })),
          api.get("/data/wells").catch(() => ({ data: [] })),
          api.get("/data/well_readings").catch(() => ({ data: [] })),
        ]);
        setRecent({
          reports: reports.data.filter((r) => (r.authority || "").toUpperCase().includes(authority)).slice(0, 3),
          racs: racs.data.filter((r) => r.status === "Cerrada").slice(0, 5),
          walks: walks.data.slice(0, 5),
          wells: wells.data,
          wellReads: wellReads.data,
        });
      } catch (e) { console.error(e); }
    })();
  }, [authority]);

  // Live search across modules
  useEffect(() => {
    if (!search || search.length < 2) { setSearchHits(null); return; }
    const q = search.toLowerCase();
    (async () => {
      try {
        const colls = ["legal_permits", "racs", "field_walks", "studies", "periodic_reports",
                       "waste_logs", "manifests", "esg_complaints", "advc_records"];
        const results = [];
        for (const c of colls) {
          const { data } = await api.get(`/data/${c}`).catch(() => ({ data: [] }));
          for (const d of data) {
            const text = JSON.stringify(d).toLowerCase();
            if (text.includes(q)) {
              results.push({ collection: c, doc: d });
              if (results.length >= 30) break;
            }
          }
          if (results.length >= 30) break;
        }
        setSearchHits(results);
      } catch (e) { console.error(e); }
    })();
  }, [search]);

  if (!summary) return <div className="audit-mode-root"><div className="p-8">Cargando Modo Auditoría…</div></div>;

  const ica = summary.ecs_score || 0;
  const icaClass = ica >= 80 ? "audit-ica-green" : ica >= 60 ? "audit-ica-yellow" : "audit-ica-red";
  const icaInterp = ica >= 80 ? "Cumplimiento óptimo" : ica >= 60 ? "Cumplimiento parcial" : "Atención inmediata";

  const filteredPermits = authority === "ALL"
    ? permits
    : permits.filter((p) => (p.authority || "").toUpperCase().includes(authority));

  const overdueFindings = (recent.racs || []).filter((r) => r.status !== "Cerrada");
  const upcomingExpiry = permits.filter((p) => {
    const days = daysUntil(p.expires_at);
    return days !== null && days >= 0 && days <= 90;
  });

  return (
    <div className="audit-mode-root" data-testid="audit-mode">
      <div className="audit-mode-header">
        <div className="audit-ica-block">
          <div className={`audit-ica-circle ${icaClass}`} data-testid="audit-ica-circle">{ica}</div>
          <div>
            <div className="text-xs text-dim">{tenant?.installation_name || tenant?.name || "Instalación"}</div>
            <h1 style={{ margin: 0, fontSize: 22 }}>Modo Auditoría</h1>
            <div className="text-xs text-dim mt-2">{icaInterp} · {now.toLocaleString("es-MX")}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm" onClick={() => setShowActaForm(true)} data-testid="audit-acta-btn"><FilePlus size={14} /> Generar Acta de Visita</button>
          <button className="btn btn-sm" onClick={async () => downloadOnePager(authority)} data-testid="audit-onepager-btn"><FileText size={14} /> One-Pager Inspector</button>
          <button className="btn btn-sm" onClick={async () => downloadExpediente(authority)} data-testid="audit-export-btn"><Download size={14} /> Exportar expediente</button>
          <button className="btn btn-danger" onClick={() => navigate("/dashboard")} data-testid="audit-exit-btn"><X size={14} /> Salir de Modo Auditoría</button>
        </div>
      </div>

      <div className="audit-kpi-row">
        <div className="audit-kpi" data-testid="audit-kpi-noncomply" onClick={() => setAuthority("PROFEPA")}>
          <div className="text-xs text-dim">🔴 Incumplimientos activos</div>
          <div className="num" style={{ color: "var(--danger)" }}>{(summary.critical_legal_unmet || 0) + overdueFindings.length}</div>
          <div className="text-xs text-dim">Ver detalle →</div>
        </div>
        <div className="audit-kpi" data-testid="audit-kpi-upcoming">
          <div className="text-xs text-dim">🟡 Vencimientos próximos (90d)</div>
          <div className="num" style={{ color: "var(--warn)" }}>{upcomingExpiry.length}</div>
          <div className="text-xs text-dim">Ver detalle →</div>
        </div>
        <div className="audit-kpi" data-testid="audit-kpi-permits">
          <div className="text-xs text-dim">✅ Permisos vigentes</div>
          <div className="num" style={{ color: "var(--accent)" }}>{permits.filter((p) => daysUntil(p.expires_at) > 0).length}</div>
          <div className="text-xs text-dim">Ver detalle →</div>
        </div>
        <div className="audit-kpi" data-testid="audit-kpi-reports">
          <div className="text-xs text-dim">📋 Informes entregados YTD</div>
          <div className="num">{(recent.reports || []).length}</div>
          <div className="text-xs text-dim">Ver detalle →</div>
        </div>
      </div>

      <div className="mb-3" style={{ position: "relative" }}>
        <Search size={16} style={{ position: "absolute", left: 12, top: 14, color: "var(--text-dim)" }} />
        <input
          className="audit-search"
          style={{ paddingLeft: 36 }}
          placeholder="¿Qué busca el inspector? (manifiesto, análisis de agua, ruido, reforestación…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="audit-search"
        />
        {searchHits !== null && (
          <div className="card mt-2" data-testid="audit-search-results">
            <div className="text-xs text-dim mb-2">{searchHits.length} resultados</div>
            {searchHits.map((h, i) => (
              <div key={i} className="flex items-center justify-between p-2" style={{ borderBottom: "1px solid var(--border)" }}>
                <div className="text-sm flex-1">
                  <b>{h.collection}</b> · {h.doc.code || h.doc.name || h.doc.id}
                  <div className="text-xs text-dim" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {JSON.stringify(h.doc).slice(0, 120)}
                  </div>
                </div>
                <button className="btn btn-sm" onClick={() => { setSearch(""); setSearchHits(null); navigate(`/${moduleRouteFor(h.collection)}?focus=${h.doc.id}`); }}>
                  Ir →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="tabs-bar mb-3">
        {AUTHORITIES.map((a) => (
          <button
            key={a.key}
            className={`tab ${authority === a.key ? "active" : ""}`}
            onClick={() => setAuthority(a.key)}
            data-testid={`audit-tab-${a.key}`}
          >{a.label}</button>
        ))}
      </div>

      <div className="audit-grid">
        <div>
          <div className="card">
            <div className="card-title">Permisos e instrumentos vigentes — {authority}</div>
            {!filteredPermits.length ? (
              <div className="text-dim text-sm p-3">Sin permisos registrados para esta autoridad.</div>
            ) : filteredPermits.map((p) => {
              const days = daysUntil(p.expires_at);
              const badgeClass = days < 0 ? "badge-red" : days < 30 ? "badge-red" : days < 90 ? "badge-yellow" : "badge-green";
              const cumpl = p.compliance_pct ?? 100;
              return (
                <div key={p.id} className="audit-permit-row" data-testid={`audit-permit-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-xs text-dim">{p.official_number || p.code} · {p.scope || ""}</div>
                    </div>
                    <span className={`badge ${badgeClass}`}>
                      {days < 0 ? `Venció hace ${-days}d` : `Vence en ${days}d`}
                    </span>
                  </div>
                  <div className="audit-bar"><div style={{ width: `${cumpl}%` }} /></div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-dim">Cumplimiento: {cumpl}%</span>
                    <div className="flex gap-1">
                      {p.active_file_id && (
                        <button className="btn btn-sm" onClick={() => setPdfPreview({ url: `${BACKEND_URL}/api/files/view/${p.active_file_id}`, name: p.name })} data-testid={`audit-view-permit-${p.id}`}>
                          <FileText size={12} /> Ver documento
                        </button>
                      )}
                      <button className="btn btn-sm" onClick={() => navigate(`/legal?focus=${p.id}`)}><ChevronRight size={12} /> Condicionantes</button>
                      {p.active_file_id && (
                        <a className="btn btn-sm" href={`${BACKEND_URL}/api/files/download/${p.active_file_id}`} download>
                          <Download size={12} /> Descargar
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card mt-3">
            <div className="card-title">Evidencias y registros recientes — {authority}</div>
            {authority === "CONAGUA" && (recent.wells || []).length > 0 ? (
              <div>
                {(recent.wells || []).map((w) => {
                  const rs = (recent.wellReads || []).find((r) => r.well_id === w.id);
                  const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
                  const ytd = rs ? months.reduce((a, m) => a + (Number(rs[m]) || 0), 0) : 0;
                  const pct = w.max_annual_m3 ? (ytd / w.max_annual_m3 * 100).toFixed(1) : "0";
                  return (
                    <div key={w.id} className="flex items-center justify-between p-2" style={{ borderBottom: "1px solid var(--border)" }}>
                      <div><b>{w.name}</b> · {w.authorization_number || "—"}</div>
                      <div><span className="badge">{ytd.toFixed(1)} M³ · {pct}%</span></div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {(recent.reports || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-2" style={{ borderBottom: "1px solid var(--border)" }}>
                <div>📑 <b>{r.name || r.type}</b> · {r.delivery_date ? new Date(r.delivery_date).toLocaleDateString("es-MX") : ""} · Acuse: {r.acuse || "—"}</div>
                {r.active_file_id && (
                  <button className="btn btn-sm" onClick={() => setPdfPreview({ url: `${BACKEND_URL}/api/files/view/${r.active_file_id}`, name: r.name })}>
                    <FileText size={12} /> Ver
                  </button>
                )}
              </div>
            ))}
            {(recent.racs || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-2" style={{ borderBottom: "1px solid var(--border)" }}>
                <div>✅ <b>{r.code || "RAC"}</b> · {r.description?.slice(0, 60) || ""}</div>
                <span className="badge badge-green">Cerrada</span>
              </div>
            ))}
          </div>
        </div>

        <div className="audit-side" data-testid="audit-side-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} style={{ color: "var(--danger)" }} />
            <b>Hallazgos pendientes</b>
          </div>
          {overdueFindings.length === 0 && upcomingExpiry.length === 0 && (
            <div className="text-dim text-xs">Sin hallazgos pendientes.</div>
          )}
          {overdueFindings.slice(0, 5).map((r) => (
            <div key={r.id} className="text-xs mb-2 p-2" style={{ background: "rgba(231,76,60,0.06)", borderRadius: 4 }}>
              <b>{r.code}</b> · {r.description?.slice(0, 50)}
            </div>
          ))}
          {upcomingExpiry.slice(0, 8).map((p) => {
            const d = daysUntil(p.expires_at);
            return (
              <div key={p.id} className="text-xs mb-2 p-2" style={{ background: "rgba(241,196,15,0.06)", borderRadius: 4 }}>
                <b>{p.name}</b><br />Vence en {d} días
              </div>
            );
          })}
        </div>
      </div>

      <Modal open={!!pdfPreview} onClose={() => setPdfPreview(null)} title={pdfPreview?.name || "Documento"} size="xl">
        {pdfPreview && (
          <div>
            <div className="flex justify-end mb-2">
              <a className="btn btn-sm" href={pdfPreview.url} target="_blank" rel="noreferrer">
                <ExternalLink size={12} /> Abrir en nueva pestaña
              </a>
            </div>
            <iframe src={pdfPreview.url} style={{ width: "100%", height: "75vh", border: 0, background: "#fff" }} title="Documento oficial" data-testid="audit-pdf-iframe" />
          </div>
        )}
      </Modal>

      <Modal open={showActaForm} onClose={() => setShowActaForm(false)} title="Generar Acta de Visita de Autoridad" size="lg">
        <ActaForm onClose={() => setShowActaForm(false)} authority={authority} />
      </Modal>
    </div>
  );
}

function ActaForm({ onClose, authority }) {
  const [form, setForm] = useState({
    visit_date: new Date().toISOString().slice(0, 10),
    authority,
    inspector_names: "",
    areas_reviewed: "",
    observations: "",
    commitments: "",
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.post("/data/authority_visits", form);
      alert("Acta de visita guardada en Atención a Visitas de Autoridad.");
      onClose();
    } catch (e) { alert("Error: " + e.message); }
    setSaving(false);
  }
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label">Fecha</label><input type="date" className="input" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} /></div>
        <div><label className="label">Autoridad</label><input className="input" value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} /></div>
      </div>
      <div><label className="label">Inspector(es)</label><input className="input" value={form.inspector_names} onChange={(e) => setForm({ ...form, inspector_names: e.target.value })} /></div>
      <div><label className="label">Áreas revisadas</label><textarea className="textarea" rows={2} value={form.areas_reviewed} onChange={(e) => setForm({ ...form, areas_reviewed: e.target.value })} /></div>
      <div><label className="label">Observaciones</label><textarea className="textarea" rows={3} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} /></div>
      <div><label className="label">Compromisos</label><textarea className="textarea" rows={3} value={form.commitments} onChange={(e) => setForm({ ...form, commitments: e.target.value })} /></div>
      <div className="flex justify-end gap-2">
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <span className="spinner" /> : "Guardar Acta"}</button>
      </div>
    </div>
  );
}

function daysUntil(d) {
  if (!d) return null;
  return Math.round((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
}

const COLLECTION_ROUTES = {
  legal_permits: "legal", periodic_reports: "legal",
  racs: "audits", field_walks: "audits", studies: "studies",
  waste_logs: "waste", manifests: "waste",
  esg_complaints: "esg", advc_records: "advc",
};
function moduleRouteFor(collection) {
  return COLLECTION_ROUTES[collection] || "dashboard";
}

async function downloadOnePager(authority) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BACKEND_URL}/api/pdf/one-pager`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ observations: `Generado para inspector — autoridad: ${authority}` }),
  });
  if (!res.ok) { alert("Error generando one-pager"); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `one_pager_${authority}.pdf`; a.click();
  URL.revokeObjectURL(url);
}

async function downloadExpediente(authority) {
  alert(`Expediente completo de ${authority} — esta función generará un ZIP en una próxima iteración. Por ahora, descarga los PDFs individualmente desde cada permiso.`);
}
